import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { createSearchParams, useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import { Button, Link, Stack, Typography } from '@mui/material';
import { Form, Field } from 'react-final-form';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import PrimaryLoadingButton from 'components/shared/buttons/PrimaryLoadingButton';
import TextFieldAdapter from 'components/shared/field-adapters/TextFieldAdapter';
import AuthScreen from 'components/auth/portal/AuthScreen';
import routes from 'common/routes';
import auth from 'api/graphql/auth';
import authSlice from 'store/modules/auth';
import uiSlice from 'store/modules/ui';
import { createSnack } from 'common/utils/general';

function VerifyResetCode() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation(['common']);
  const email = searchParams.get('email') || '';

  const [loading, setLoading] = useState(false);

  const onVerify = async ({ token }) => {
    setLoading(true);
    try {
      const response = await auth.checkPasswordResetToken(email, token);
      const { data: { checkPasswordResetToken: result } } = response;

      if (result.valid) {
        navigate({
          pathname: routes.resetPassword.path,
          search: `?${createSearchParams({ email, token })}`
        });
      } else {
        dispatch(uiSlice.actions.enqueueSnackbar(createSnack({
          key: `invalid-code-${Date.now()}`,
          variant: 'error',
          message: t('auth.invalidCode', { ns: 'common' })
        })));
      }
    } catch {
      dispatch(uiSlice.actions.enqueueSnackbar(createSnack({
        key: `invalid-code-${Date.now()}`,
        variant: 'error',
        message: t('auth.invalidCode', { ns: 'common' })
      })));
    } finally {
      setLoading(false);
    }
  };

  const onResend = () => {
    dispatch(authSlice.actions.generateResetToken(email));
  };

  return (
    <>
      <SEOPageTitle title={t('auth.verifyCode', { ns: 'common' })} />
      <AuthScreen>
        <Form
          initialValues={{ token: '' }}
          onSubmit={onVerify}
          render={({ handleSubmit, pristine }) => (
            <form onSubmit={handleSubmit}>
              <Stack spacing={4} sx={{ width: '100%' }}>
                <Stack spacing={2}>
                  <Typography variant="h5" align="center" component="h1" sx={{ fontWeight: 500 }}>
                    {t('auth.verifyCode', { ns: 'common' })}
                  </Typography>
                  <Typography variant="body2" align="center" component="p">
                    {t('auth.codeSent', { ns: 'common', email })}
                  </Typography>
                </Stack>
                <Stack spacing={1}>
                  <Field
                    id="token"
                    name="token"
                    component={TextFieldAdapter}
                    label={t('auth.codeLabel', { ns: 'common' })}
                    type="text"
                    inputProps={{ maxLength: 6 }}
                    fullWidth
                    required
                  />
                </Stack>
                <PrimaryLoadingButton
                  disabled={pristine || loading}
                  loading={loading}
                  label={t('auth.verifyCode', { ns: 'common' })}
                  type="submit"
                />
                {/* Reenviar es una ACCIÓN (no navegación): botón de texto con look de link.
                    Colores por token (antes blancos/#F9BD4E fijos que rompían el modo claro). */}
                <Button
                  variant="text"
                  onClick={onResend}
                  sx={{
                    alignSelf: 'center',
                    color: 'secondary.main',
                    fontWeight: 600,
                    textDecoration: 'underline',
                    textTransform: 'none',
                    '&:hover': { textDecoration: 'underline' }
                  }}>
                  {t('auth.resendCode', { ns: 'common' })}
                </Button>
                <Typography variant="subtitle2" align="center" sx={{ color: 'text.secondary' }}>
                  <Link component={RouterLink} to={routes.login.path} underline="always" sx={{ color: 'secondary.main' }}>
                    {t('auth.loginHere', { ns: 'common' })}
                  </Link>
                </Typography>
              </Stack>
            </form>
          )}
        />
      </AuthScreen>
    </>
  );
}

export default VerifyResetCode;
